import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import Spinner from "../../../../../../../common/Spinner";
import ErrorComponent from "../../../../../../../common/ErrorComponent";
import NoDataAvailable from "../../../../../../../common/NoDataAvailable";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import { ad_ss_heatmap_payload } from "../../../../../../../config/reqConfig/anomalyDetection";
import ApiService from "../../../../../../../config/api-config";
import { getConfig, getCellElem } from "../../../common/heatmapConfig";
import AppHoc from "../../../../../../../generics/AppHOC";
import CustomTimeStamp from "../../../../../../../generics/TimeStamp/customTimeStamp";
import SSHourlyTable from "./SSHourlyTable";
import {
  backtoSevenDays,
  displayDateTime,
} from "../../../../../../../utils/displayTime";
import { format } from "date-fns";
import { colors } from "../../../../../../../utils/colors";
import { convertToCSV } from "../../../common/adUtils";
import useI18n from "../../../../../../../i18n/useI18n";

const [HbrHeatmapChart, HbrTooltip, HbrIconButton] = reactWrapper([
  "hbr-heatmap-chart",
  "hbr-tooltip",
  "hbr-icon-button",
]);

const Heatmapview = (props) => {
  const { tViews } = useI18n("views");
  const [hmError, setHmError] = useState(null);
  const [hmData, setHmData] = useState([]);
  const [hmLoading, setHmLoading] = useState(true);
  const [timeFrame, setTimeFrame] = useState(null);
  const [hourSelected, setHourSelected] = useState(null);
  const [selectedElems, setSelectedElems] = useState({
    prev: null,
    current: null,
  });

  useEffect(() => {
    if (selectedElems.current !== selectedElems.prev) {
      selectedElems.prev?.setAttribute("stroke-width", "0");
      selectedElems.prev?.setAttribute("stroke", "none");
      selectedElems.current?.setAttribute("stroke-width", "5");
      selectedElems.current?.setAttribute("stroke", colors.blue45);
    } else {
      selectedElems.current?.setAttribute("stroke-width", "0");
      selectedElems.current?.setAttribute("stroke", "none");
    }
  }, [selectedElems]);

  useEffect(() => {
    if (hourSelected) {
      let initDayLabel = format(new Date(hourSelected), "EEE d");
      let initHourLabel = format(new Date(hourSelected), "h:mm aa");

      setTimeout(() => {
        getCellElem(
          ".ss-heatmap-chart hbr-heatmap-chart",
          initDayLabel,
          initHourLabel,
          setSelectedElems,
        );
      }, 500);
    }
  }, [hmData]);

  useEffect(() => {
    if (hourSelected) props.getLocalLocation(hourSelected);
  }, [hourSelected]);

  useEffect(() => {
    const runEffect = async () => {
      if (props.selectedSite === -1) return;
      window.scrollTo(0, 0, "smooth");
      const fetchData = async () => {
        // Fetch data for heatmap here
        try {
          setHmLoading(true);
          setHmError(null);
          let timePeriod = backtoSevenDays();
          setTimeFrame(timePeriod);
          const payload = ad_ss_heatmap_payload(timePeriod, props.selectedSite);
          const res = await ApiService.getADSingleSiteHeatmap(payload);
          if (!res.errorObject) {
            const config = getConfig(
              res,
              setHourSelected,
              timePeriod,
              setSelectedElems,
              props.adKey,
              props.actions,
              tViews("adChartsID.tunnelChartID"),
            );
            setHmData(config);
          } else {
            setHmError(res.errorObject);
          }
        } catch (error) {
          setHmError(error);
        }
        setHmLoading(false);
      };
      fetchData();
    };

    runEffect();
  }, [props.selectedSite, props.globalFilter.onAnomalyRefresh]);

  return (
    <>
      <div className="ss-heatmapview">
        {hmLoading ? (
          <Spinner />
        ) : hmError ? (
          <ErrorComponent error={hmError} />
        ) : hmData.data.length === 0 ? (
          <NoDataAvailable />
        ) : (
          <>
            <div className="ss-heatmap-timeFrame">
              <CustomTimeStamp
                className="timestamp"
                prevTime={displayDateTime(timeFrame[0])}
                currentTime={displayDateTime(timeFrame[1])}
              />
              <div className="ad-ss-timePeriodTxt">{`${tViews("app360.afHeatmapTimePriodText")}`}</div>
            </div>
            <div className="ss-heatmap-chart">
              <HbrHeatmapChart config={hmData} width={"100%"}>
                <div slot="header-action">
                  <HbrTooltip content={`${tViews("downLoadCsv")}`}>
                    <span>
                      <HbrIconButton
                        name={`${tViews("csvFile")}`}
                        size="20px"
                        arialabel={`${tViews("downloadData")}`}
                        onClick={() =>
                          convertToCSV(hmData.data, `${tViews("adSsHeatmap")}`)
                        }
                      />
                    </span>
                  </HbrTooltip>
                </div>
              </HbrHeatmapChart>
            </div>
            {hourSelected && (
              <SSHourlyTable
                selectedSite={props.selectedSite}
                selectedHour={hourSelected}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

Heatmapview.propTypes = {
  selectedSite: PropTypes.number,
  globalFilter: PropTypes.object,
  actions: PropTypes.object,
  adKey: PropTypes.object,
  getLocalLocation: PropTypes.func,
};

export default AppHoc(Heatmapview);
